import type { FC, PropsWithChildren, ReactNode } from "react";

import { cn } from "@/lib/utils";
import { m } from "@recobra/i18n/messages";
import { Item, ItemContent, ItemMedia } from "@recobra/ui/item";

interface TableOfContentsSection {
  id: string;
  label: ReactNode;
  hasError?: boolean;
}

export const FormPageTableOfContents: FC<{
  sections: TableOfContentsSection[];
}> = ({ sections }) => (
  <aside className="sticky top-6 hidden w-48 min-w-0 self-start lg:block 2xl:w-52">
    <nav className="rounded-lg border bg-card p-3 xl:p-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
        {m.case_toc_title()}
      </p>
      <ul className="flex flex-col gap-0.5">
        {sections.map((sec, i) => (
          <li key={sec.id}>
            <Item
              size="xs"
              render={<a href={`#${sec.id}`} />}
              onClick={(event) => {
                event.preventDefault();
                document.getElementById(sec.id)?.scrollIntoView({
                  block: "start",
                  behavior: "smooth",
                });
              }}
            >
              <ItemMedia>
                <span
                  className={cn(
                    "font-mono text-xs",
                    sec.hasError ? "text-destructive" : "text-muted-foreground",
                  )}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
              </ItemMedia>
              <ItemContent>
                <span className={sec.hasError ? "text-destructive" : undefined}>{sec.label}</span>
              </ItemContent>
            </Item>
          </li>
        ))}
      </ul>
    </nav>
  </aside>
);

export const FormPageLayoutGrid: FC<
  PropsWithChildren<{
    aside?: ReactNode;
  }>
> = ({ children, aside }) => (
  <div
    className={cn(
      "grid gap-5 px-4 py-5 sm:px-6 sm:py-6",
      aside
        ? "w-full lg:grid-cols-[minmax(1rem,1fr)_minmax(0,38rem)_minmax(12rem,1fr)] 2xl:grid-cols-[minmax(1rem,1fr)_minmax(0,44rem)_minmax(13rem,1fr)]"
        : "mx-auto w-full max-w-2xl",
    )}
  >
    <div className={cn("flex min-w-0 w-full flex-col gap-5 lg:gap-6", aside && "lg:col-start-2")}>
      {children}
    </div>

    {aside ? <div className="hidden min-w-0 lg:col-start-3 lg:block">{aside}</div> : null}
  </div>
);
